import type { InjectionKey } from 'vue';
import type {
	DataListColumnVisibility,
	DataListRow,
	DataListSortState,
} from '../DataList/DataList.model';
import type { AdvancedFilterLayoutMode } from '../AdvancedFiltersLayout/AdvancedFiltersLayout.model';

/**
 * What the package needs in order to remember a user's UI preferences.
 *
 * Declared here rather than imported from an app: the store that satisfies it
 * belongs to whichever app is running. Components treat it as optional — with
 * no store provided they render normally and simply remember nothing.
 */
export interface PreferencesStore {
	getPerPage: (key: string) => number | null;
	setPerPage: (key: string, perPage: number) => void;
	clearPerPage: (key: string) => void;

	getSort: (
		key: string
	) => { prop: string; order: DataListSortState['order'] } | null;
	setSort: (
		key: string,
		payload: { prop: string; order: DataListSortState['order'] }
	) => void;
	clearSort: (key: string) => void;

	getColumnOrder: (key: string) => string[] | null;
	setColumnOrder: (key: string, order: string[]) => void;
	clearColumnOrder: (key: string) => void;

	getColumnVisibility: (key: string) => DataListColumnVisibility[] | null;
	setColumnVisibility: (
		key: string,
		visibility: DataListColumnVisibility[]
	) => void;
	clearColumnVisibility: (key: string) => void;

	getColumnWidths: (
		key: string
	) => Array<{ key: string; width: number }> | null;
	setColumnWidths: (
		key: string,
		widths: Array<{ key: string; width: number }>
	) => void;
	clearColumnWidths: (key: string) => void;

	getDataListSelection: (key: string) => DataListRow[];
	setDataListSelection: (key: string, rows: DataListRow[]) => void;
	clearDataListSelection: (key: string) => void;
	getDataListSelectionPanelOpen: (key: string) => boolean;
	setDataListSelectionPanelOpen: (key: string, isOpen: boolean) => void;

	getAdvancedFilterLayoutMode: (key: string) => AdvancedFilterLayoutMode | null;
	setAdvancedFilterLayoutMode: (
		key: string,
		mode: AdvancedFilterLayoutMode
	) => void;
	getAdvancedFilterCollapseOpen: (key: string) => boolean | null;
	setAdvancedFilterCollapseOpen: (key: string, isOpen: boolean) => void;
}

export const preferencesStoreKey: InjectionKey<PreferencesStore> = Symbol(
	'ar-preferences-store'
);
